import { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';
import { Congrats, useLoungeCountdown, LoungeCountdown } from '@/lib/loungeGame';
import { recordRun, playSfx } from '@/lib/loungeMeta';

/**
 * BoxBreathing — a 4-4-4-4 box-breath widget.
 *
 *  • Simple explanation + "Begin" button (does NOT auto-start)
 *  • Big animated square with a light that traces its perimeter
 *  • Phase label + seconds countdown + cycle counter
 *  • Pause / Reset controls, keyboard: Space toggles play/pause
 */

const PHASES = [
  { key: 'inhale',   label: 'Breathe in',  dur: 4000, side: 'top'    },
  { key: 'hold1',    label: 'Hold',        dur: 4000, side: 'right'  },
  { key: 'exhale',   label: 'Breathe out', dur: 4000, side: 'bottom' },
  { key: 'hold2',    label: 'Hold',        dur: 4000, side: 'left'   },
];
const CYCLE_MS = PHASES.reduce((a, p) => a + p.dur, 0);

function resolvePhase(elapsed) {
  let acc = 0;
  for (let i = 0; i < PHASES.length; i++) {
    if (elapsed < acc + PHASES[i].dur) {
      return { ix: i, progress: (elapsed - acc) / PHASES[i].dur };
    }
    acc += PHASES[i].dur;
  }
  return { ix: PHASES.length - 1, progress: 1 };
}

function tracerPos(ix, p) {
  if (ix === 0) return { x: p * 100, y: 0 };
  if (ix === 1) return { x: 100, y: p * 100 };
  if (ix === 2) return { x: 100 - p * 100, y: 100 };
  return { x: 0, y: 100 - p * 100 };
}

export default function BoxBreathing() {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [cycles, setCycles] = useState(0);
  const [showCongrats, setShowCongrats] = useState(false);
  const congratsFiredRef = useRef(false);
  const startAtRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!running) return;
    const tick = (ts) => {
      const now = ts - startAtRef.current;
      setElapsed(now);
      const c = Math.floor(now / CYCLE_MS);
      setCycles((prev) => {
        if (c >= 3 && !congratsFiredRef.current) {
          congratsFiredRef.current = true;
          setRunning(false);
          setShowCongrats(true);
          recordRun('breath', { cycles: c });
          playSfx('levelup');
        }
        return c !== prev ? c : prev;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    startAtRef.current = performance.now() - elapsed;
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const { countdown, beginCountdown, cancelCountdown } = useLoungeCountdown();

  const begin = () => {
    setElapsed(0);
    setCycles(0);
    congratsFiredRef.current = false;
    setShowCongrats(false);
    // 3-2-1-GO countdown first, then start the breathing loop.
    beginCountdown(() => setRunning(true));
  };
  const pause = () => { cancelCountdown(); setRunning(false); };
  const reset = () => {
    cancelCountdown();
    setRunning(false);
    setElapsed(0);
    setCycles(0);
    congratsFiredRef.current = false;
    setShowCongrats(false);
  };

  const cardRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== 'Space') return;
      const active = document.activeElement;
      if (active && ['INPUT', 'TEXTAREA', 'BUTTON'].includes(active.tagName)) return;
      if (!cardRef.current) return;
      const rect = cardRef.current.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (!inView) return;
      e.preventDefault();
      if (running) pause(); else begin();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const { ix, progress } = useMemo(() => resolvePhase(elapsed % CYCLE_MS), [elapsed]);
  const cur = PHASES[ix];
  const secondsLeft = running ? Math.max(1, Math.ceil((cur.dur - progress * cur.dur) / 1000)) : 4;
  const tracer = tracerPos(ix, progress);

  return (
    <section
      ref={cardRef}
      className="max-w-3xl mx-auto bg-white rounded-3xl card-soft border border-navy/5 overflow-hidden"
      data-testid="box-breathing"
    >
      {/* Hero banner */}
      <div className="relative w-full aspect-[16/9] overflow-hidden"
           style={{ background: 'linear-gradient(135deg, #0B192C 0%, #12253F 100%)' }}
           data-testid="box-breathing-banner">
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-4 text-center">
          <p className="text-[10px] uppercase tracking-[0.32em] text-gold font-semibold">Lock In</p>
          <p className="font-heading text-2xl lg:text-3xl text-white/90 mt-2">Breathe and focus</p>
        </div>
        <img
          src="/images/lounge/lock-in.png"
          alt="Lock In — Breathe and focus."
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      </div>

      <div className="p-5 lg:p-7">
        <h2 className="sr-only">Lock In</h2>
        <p className="text-sm lg:text-base text-navy/70 max-w-xl leading-relaxed">
          A simple technique used by Navy SEALs, athletes, and therapists to calm the nervous system in under a minute.
          You breathe in for four seconds, hold for four, breathe out for four, and hold for four — tracing the sides of a box.
          <span className="block mt-2 text-navy/55 italic">Tip: sit tall, relax your shoulders, breathe through your nose. Three cycles is plenty when you&apos;re stressed; five to ten before a hard conversation.</span>
        </p>

        <div className="mt-6 relative grid grid-cols-1 md:grid-cols-[320px_1fr] gap-6 lg:gap-10 items-center">
          <div className="relative mx-auto md:mx-0 h-[300px] w-[300px] lg:h-[360px] lg:w-[360px]">
            <div className="absolute inset-0 rounded-3xl border-2 border-navy/15 bg-beige/40" />
            <ActiveSide side={cur.side} progress={running ? progress : 0} />
            <div
              className="absolute h-4 w-4 rounded-full bg-gold shadow-md ring-4 ring-gold/25 pointer-events-none"
              style={{
                left: `${tracer.x}%`,
                top: `${tracer.y}%`,
                transform: 'translate(-50%, -50%)',
                transition: running ? 'none' : 'left 300ms ease, top 300ms ease',
              }}
              data-testid="box-breathing-tracer"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
              <p className="text-[10px] uppercase tracking-[0.28em] text-navy/50 font-semibold">
                {running ? 'Now' : 'Ready'}
              </p>
              <p className="font-heading text-3xl lg:text-4xl text-navy mt-1" data-testid="box-breathing-phase">
                {running ? cur.label : 'Breathe in'}
              </p>
              <p className="text-5xl lg:text-6xl font-heading text-gold mt-1 tabular-nums" data-testid="box-breathing-seconds">
                {secondsLeft}
              </p>
            </div>
            {/* 3-2-1-GO countdown (shared across all Lounge games) */}
            <LoungeCountdown n={countdown} testid="box-breathing-countdown" color="#C5A059" />
          </div>

          <div className="flex flex-col items-center gap-3 w-full max-w-[280px] mx-auto md:mx-0 md:pl-4">
            {!running ? (
              <button
                onClick={begin}
                data-testid="box-breathing-begin"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white font-semibold hover:bg-navy-soft transition-smooth text-sm w-full justify-center"
              >
                <Play size={14} strokeWidth={2.4}/> {cycles > 0 ? 'Resume' : 'Begin'}
              </button>
            ) : (
              <button
                onClick={pause}
                data-testid="box-breathing-pause"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white font-semibold hover:bg-navy-soft transition-smooth text-sm w-full justify-center"
              >
                <Pause size={14} strokeWidth={2.4}/> Pause
              </button>
            )}
            <button
              onClick={reset}
              data-testid="box-breathing-reset"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-navy border border-navy/15 hover:border-navy/30 transition-smooth text-sm w-full justify-center"
            >
              <RotateCcw size={13} strokeWidth={2}/> Reset
            </button>
            <span className="sr-only" data-testid="box-breathing-cycles">{cycles}</span>
          </div>
        </div>
        <Congrats
          open={showCongrats}
          title="Three rounds in"
          subtitle="Nervous system reset. Nice work — walk into whatever&rsquo;s next a little calmer."
          onPlayAgain={begin}
          testid="box-breathing-congrats"
        />
      </div>
    </section>
  );
}

function ActiveSide({ side, progress }) {
  const common = 'absolute bg-gold/70 rounded-full';
  const p = Math.max(0.02, progress);
  if (side === 'top') return <div className={common} style={{ left: 0, top: -2, height: 4, width: `${p * 100}%` }} />;
  if (side === 'right') return <div className={common} style={{ right: -2, top: 0, width: 4, height: `${p * 100}%` }} />;
  if (side === 'bottom') return <div className={common} style={{ right: 0, bottom: -2, height: 4, width: `${p * 100}%` }} />;
  return <div className={common} style={{ left: -2, bottom: 0, width: 4, height: `${p * 100}%` }} />;
}
